import { FC, useState } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';

interface SavePlanModalProps {
  open: boolean;
  onClose: () => void;
  /** Return false to abort without closing (e.g. validation failed). */
  onSave: (name: string, description?: string) => Promise<boolean | void>;
  isLoading?: boolean;
}

export const SavePlanModal: FC<SavePlanModalProps> = ({ open, onClose, onSave, isLoading }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const shouldComplete = await onSave(values.name, values.description);
      if (shouldComplete === false) {
        return;
      }
      message.success('Plan saved successfully!');
      form.resetFields();
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message || 'Failed to save plan');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Save Calculator Plan"
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={loading || isLoading} onClick={handleSubmit}>
          Save Plan
        </Button>
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Plan Name"
          rules={[{ required: true, message: 'Please enter a plan name' }]}
        >
          <Input placeholder="e.g., Q1 2024 Campaign Plan" />
        </Form.Item>
        <Form.Item name="description" label="Description (Optional)">
          <Input.TextArea rows={3} placeholder="Add notes or description about this plan" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
